import { useCallback, useMemo, useRef, useState } from 'react';
import type { DocumentConflictStrategy, PayslipType } from '@timesheet/schemas';
import { DocumentApiError, documentsService } from '../../../services/documentsService';
import { payslipBalanceService } from '../../../services/payslipBalanceService';

export interface PayslipPeriod {
  type: PayslipType;
  year: number;
  /** undefined for CU (yearly). */
  month?: number;
}

export type PayslipJobStatus = 'pending' | 'uploading' | 'done' | 'conflict' | 'retrying' | 'error';

export interface PayslipJob {
  id: string;
  file: File;
  userId: number;
  userName: string;
  userInactive: boolean;
  status: PayslipJobStatus;
  errorCode?: string;
  documentId?: number;
  replaced?: boolean;
  balances?: 'saved' | 'failed' | 'skipped';
}

export interface PayslipJobsSummary {
  total: number;
  done: number;
  replaced: number;
  conflicts: number;
  errors: number;
  balancesFailed: number;
}

const CONCURRENCY = 2;
const MAX_RETRIES = 3;

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const toNullableNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Runs the client-side balance extraction and persists it; failures never affect the document. */
async function persistBalances(job: PayslipJob, period: PayslipPeriod, documentId: number): Promise<'saved' | 'failed'> {
  try {
    const { analyzePayslipPdfQuick } = await import('../../../lib/payslipQuickAnalysis');
    const analysis = await analyzePayslipPdfQuick(job.file);
    const domain = (values: { precedente: unknown; maturato: unknown; goduto: unknown; residuo: unknown; proiezione: unknown }) => ({
      precedente: toNullableNumber(values.precedente),
      maturato: toNullableNumber(values.maturato),
      goduto: toNullableNumber(values.goduto),
      residuo: toNullableNumber(values.residuo),
      proiezione: toNullableNumber(values.proiezione),
    });
    await payslipBalanceService.upsert({
      userId: job.userId,
      documentId,
      payslipMonth: period.month as number,
      payslipYear: period.year,
      hoursPerDay: analysis.computed.hoursPerDayUsed,
      source: 'upload_auto_analysis',
      ferie: domain(analysis.ferie),
      permessi: domain(analysis.permessi),
      rol: domain(analysis.rol),
      exFest: domain(analysis.exFest),
      units: {
        ferie: analysis.units?.ferie ?? null,
        permessi: analysis.units?.permessi ?? null,
        rol: analysis.units?.rol ?? null,
        exFest: analysis.units?.exFest ?? null,
      },
      computed: {
        totalDaysResidual: toNullableNumber(analysis.computed?.totalDaysResidual),
        totalDaysProjection: toNullableNumber(analysis.computed?.totalDaysProjection),
      },
    });
    return 'saved';
  } catch (error) {
    console.error('Payslip balance extraction failed:', error);
    return 'failed';
  }
}

/**
 * Sequential upload queue (concurrency 2) with per-row conflict handling:
 * 409 → the row waits for "replace" / "keep"; 429 → retried honouring Retry-After.
 */
export function usePayslipUploadJobs(period: PayslipPeriod, extractBalances: boolean) {
  const [jobs, setJobs] = useState<PayslipJob[]>([]);
  const [running, setRunning] = useState(false);
  const jobsRef = useRef<PayslipJob[]>([]);

  const patch = useCallback((id: string, changes: Partial<PayslipJob>) => {
    jobsRef.current = jobsRef.current.map((job) => (job.id === id ? { ...job, ...changes } : job));
    setJobs(jobsRef.current);
  }, []);

  const runOne = useCallback(
    async (job: PayslipJob, onConflict?: DocumentConflictStrategy) => {
      patch(job.id, { status: 'uploading', errorCode: undefined });
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          const doc = await documentsService.upload(
            {
              file: job.file,
              category: 'BUSTE_PAGA',
              targetUserId: job.userId,
              payslipType: period.type,
              payslipYear: period.year,
              payslipMonth: period.month,
            },
            onConflict,
          );
          const shouldExtract = extractBalances && period.type === 'MENSILE' && period.month !== undefined;
          const balances = shouldExtract ? await persistBalances(job, period, doc.id) : 'skipped';
          patch(job.id, { status: 'done', documentId: doc.id, replaced: onConflict === 'replace', balances });
          return;
        } catch (error) {
          if (error instanceof DocumentApiError && error.status === 409) {
            patch(job.id, { status: 'conflict' });
            return;
          }
          if (error instanceof DocumentApiError && error.status === 429 && attempt < MAX_RETRIES) {
            patch(job.id, { status: 'retrying' });
            await sleep(Math.max(1, error.retryAfterSeconds ?? 2) * 1000);
            continue;
          }
          patch(job.id, { status: 'error', errorCode: error instanceof DocumentApiError ? error.code : 'generic' });
          return;
        }
      }
    },
    [patch, period, extractBalances],
  );

  const start = useCallback(
    async (initial: Omit<PayslipJob, 'status'>[]) => {
      jobsRef.current = initial.map((job) => ({ ...job, status: 'pending' as const }));
      setJobs(jobsRef.current);
      setRunning(true);
      const queue = [...jobsRef.current];
      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (queue.length > 0) {
          const next = queue.shift();
          if (next) await runOne(next);
        }
      });
      await Promise.all(workers);
      setRunning(false);
    },
    [runOne],
  );

  const resolveConflict = useCallback(
    async (id: string, strategy: DocumentConflictStrategy) => {
      const job = jobsRef.current.find((j) => j.id === id);
      if (!job) return;
      setRunning(true);
      await runOne(job, strategy);
      setRunning(false);
    },
    [runOne],
  );

  const replaceAll = useCallback(async () => {
    const conflicts = jobsRef.current.filter((j) => j.status === 'conflict');
    setRunning(true);
    for (const job of conflicts) {
      await runOne(job, 'replace');
    }
    setRunning(false);
  }, [runOne]);

  const retry = useCallback(
    async (id: string) => {
      const job = jobsRef.current.find((j) => j.id === id);
      if (!job) return;
      setRunning(true);
      await runOne(job);
      setRunning(false);
    },
    [runOne],
  );

  const summary = useMemo<PayslipJobsSummary>(
    () => ({
      total: jobs.length,
      done: jobs.filter((j) => j.status === 'done').length,
      replaced: jobs.filter((j) => j.status === 'done' && j.replaced).length,
      conflicts: jobs.filter((j) => j.status === 'conflict').length,
      errors: jobs.filter((j) => j.status === 'error').length,
      balancesFailed: jobs.filter((j) => j.balances === 'failed').length,
    }),
    [jobs],
  );

  return { jobs, running, start, resolveConflict, replaceAll, retry, summary };
}
